import { zodResolver } from '@hookform/resolvers/zod';
import { PoleKoduSps } from '@/components/PoleKoduSps';
import { WyborOperacjiGrupy } from '@/components/WyborOperacjiGrupy';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Boxes, Clock, Gauge, KeyRound, KeySquare, Loader2, Pencil, Plus, Split, Trash2, UserX, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { OperacjeModal } from '@/components/OperacjeModal';
import { ZmianaKoduGrupy } from '@/components/ZmianaKoduGrupy';
import { UsuniecieGrupyPracownikow } from '@/components/UsuniecieGrupyPracownikow';
import { TransferPicker, toggleIn } from '@/components/TransferPicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  createGroup,
  fetchCandidates,
  fetchGroupMachines,
  fetchMembers,
  fetchWithoutGroup,
  checkGroupCode,
  listGroups,
  updateGroup,
  updateGroupMachines,
  updateMembers,
  type Candidate,
  type GroupRow,
  type KursorKandydatow,
  type WithoutGroupRow,
} from '@/lib/employee-groups-api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';

// ─────────────────────────── formularz grupy ─────────────────────────────────

/**
 * Kod grupy jest WPISYWANY, nie generowany.
 *
 * Wcześniej formularz pokazywał „grupa dostanie numer N" z `MAX(USERGROUPID)+1`. Kod grupy
 * w SPS (`UserGroups.USERGROUPNUMBER`) to TEKST i u klientów zwykle SŁOWO — 'BIURO',
 * 'PRODU' — więc nie ma czego podpowiadać. Portal może tylko sprawdzić kolizję.
 */
const FormSchema = z.object({
  spsGroupNumber: z
    .string()
    .trim()
    .min(1, 'Podaj kod grupy')
    .max(32, 'Najwyżej 32 znaki — SPS obciąłby dłuższy bez ostrzeżenia'),
  name: z.string().trim().min(1, 'Podaj nazwę grupy').max(50),
});
type FormValues = z.infer<typeof FormSchema>;

function GroupModal({
  open,
  onOpenChange,
  editing,
  groupId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: GroupRow | null;
  groupId: string;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);
  /**
   * ⚠️ STAN POZA FORMULARZEM, bo to nie pole tekstowe, a zbiór zaznaczeń — wciśnięcie go
   * w schemat zoda kazałoby walidować listę kodów, których słownik i tak przychodzi z bazy
   * klienta i portal go nie zna z góry.
   */
  const [operacje, setOperacje] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { spsGroupNumber: editing?.spsGroupNumber ?? '', name: editing?.name ?? '' },
  });

  /**
   * ⚠️⚠️ DWIE WARTOŚCI, NIE JEDNA, i to nie jest nadmiar. `kodWpisany` idzie do POLA, więc NIE
   * może być przycinana: gdyby była, spacja między wyrazami znikałaby w chwili wpisania
   * (bo w tej chwili jest na końcu wartości), a spacja w środku kodu jest dozwolona.
   * `codeTyped` — przycięta — służy sprawdzeniu kolizji, bo o kolizji decyduje postać DO ZAPISU.
   */
  const kodWpisany = watch('spsGroupNumber') ?? '';
  const codeTyped = kodWpisany.trim();

  /**
   * Sprawdzenie kolizji kodu — ZASTĄPIŁO podpowiadanie numeru.
   *
   * Patrzy też na kolejkę config-down: kod może być wolny w read-modelu i jednocześnie zajęty
   * operacją, która czeka na wykonanie. Porównanie jest niewrażliwe na wielkość liter, bo taka
   * jest kolacja SPS — dla bazy klienta 'BIURO' i 'biuro' to JEDEN wiersz.
   */
  const codeCheck = useQuery({
    queryKey: ['employee-groups', 'check-code', groupId, codeTyped],
    queryFn: () => checkGroupCode(groupId, codeTyped),
    enabled: open && editing === null && codeTyped.length > 0,
  });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      if (editing) {
        await updateGroup(editing.id, values.name);
        toast.success(t('grp.saved'));
      } else {
        const res = await createGroup({
          groupId,
          spsGroupNumber: values.spsGroupNumber,
          name: values.name,
          memberIds: [],
          operacje,
        });
        toast.success(`${t('grp.created')} (${res.spsGroupNumber})`);
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : t('common.error'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={editing ? t('grp.editTitle') : t('grp.addTitle')}
        /**
         * ⚠️ BEZ OPISU PRZY ZAKŁADANIU — na żądanie. Stało tu zdanie o tym, że kod nadaje
         * człowiek i że ma najwyżej 32 znaki, i to samo zdanie POWTARZAŁO się pod polem. Reguła
         * jest teraz jedna, przy polu, i pilnuje jej samo pole (`PoleKoduSps`).
         */
        description={editing ? `${t('grp.code')}: ${editing.spsGroupNumber}` : undefined}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormGrid>
            {/* Kod grupy jest edytowalny TYLKO przy tworzeniu: to klucz naturalny w SPS,
                a jego zmiana na istniejącej grupie to nie edycja, tylko przeniesienie
                wszystkich członkostw i limitów na inny rekord. */}
            {!editing && (
              <Field
                label={t('grp.code')}
                htmlFor="spsGroupNumber"
                required
                error={errors.spsGroupNumber?.message}
              >
                {/*
                  ⚠️⚠️ POLE PILNUJE SIĘ SAMO: 32 znaki, bez polskich liter, spacja w środku wolno.
                  Przycinanie brzegów robi schemat (`.trim()` w zodzie) — dopiero przy zapisie,
                  bo cięcie na bieżąco nie pozwoliłoby wpisać spacji w środku.
                */}
                <PoleKoduSps
                  id="spsGroupNumber"
                  autoFocus
                  placeholder="np. BIURO"
                  value={kodWpisany}
                  onChange={(v) => setValue('spsGroupNumber', v, { shouldValidate: true })}
                />
                {codeTyped.length > 0 && codeCheck.data && !codeCheck.data.free && (
                  <p className="mt-1 text-xs text-[var(--destructive)]">
                    {t('grp.codeTaken').replace('{name}', codeCheck.data.takenBy?.name ?? '?')}
                  </p>
                )}
                {codeTyped.length > 0 && codeCheck.data?.free && codeCheck.data.pending && (
                  <p className="mt-1 text-xs text-[var(--warning)]">{t('grp.codePending')}</p>
                )}
              </Field>
            )}
            <Field label={t('grp.name')} htmlFor="name" required error={errors.name?.message}>
              <Input id="name" autoComplete="off" autoFocus={!!editing} {...register('name')} />
            </Field>
            {editing && editing.rationingCount > 0 && (
              <div className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
                {t('grp.limitsWarn').replace('{n}', String(editing.rationingCount))}
              </div>
            )}
            {/*
              ⚠️⚠️ UPRAWNIENIA OD RAZU, W TYM SAMYM ŻĄDANIU — zamówione 04.09.2026. Do tej pory
              grupa powstawała bez nich i trafiała w tym stanie do bazy klienta, a operacje
              nadawało się w drugim oknie. Przy EDYCJI tego tu nie ma: istniejąca grupa ma własny
              ekran uprawnień, a druga droga do tych samych kolumn to dwie wersje jednej reguły.
            */}
            {!editing && (
              <Field label={t('grp.opsLabel')} htmlFor="operacje">
                <WyborOperacjiGrupy groupId={groupId} wybrane={operacje} onZmiana={setOperacje} />
              </Field>
            )}
            <ModalError>{serverError}</ModalError>
          </FormGrid>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── picker składu ───────────────────────────────────

function MembersModal({
  group,
  islandId,
  onClose,
  onSaved,
}: {
  group: GroupRow;
  islandId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [toAdd, setToAdd] = useState<Set<string>>(new Set());
  const [toRemove, setToRemove] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  const members = useQuery({ queryKey: ['employee-groups', group.id, 'members'], queryFn: () => fetchMembers(group.id) });

  /**
   * Kandydaci porcjami — stan doczytywania trzymamy tutaj, a nie w cache zapytania.
   *
   * ⚠️ KURSOR MUSI ZNIKAĆ PRZY ZMIANIE SZUKAJKI. Kursor to „ostatnie zobaczone nazwisko";
   * po zawężeniu szukajki stary kursor odciąłby początek NOWEGO wyniku i lista wróciłaby pusta
   * przy poprawnie działającym filtrze — objaw wyglądający jak „szukajka nic nie znajduje".
   */
  const [porcje, setPorcje] = useState<Candidate[]>([]);
  const [kursor, setKursor] = useState<KursorKandydatow>(null);
  const [total, setTotal] = useState(0);
  const [doczytuje, setDoczytuje] = useState(false);

  const candidates = useQuery({
    queryKey: ['employee-groups', group.id, 'candidates', debounced],
    queryFn: () => fetchCandidates(group.id, islandId, debounced),
  });

  useEffect(() => {
    if (!candidates.data) return;
    setPorcje(candidates.data.rows);
    setKursor(candidates.data.next);
    setTotal(candidates.data.total);
  }, [candidates.data]);

  const doczytaj = async () => {
    if (!kursor || doczytuje) return;
    setDoczytuje(true);
    try {
      const kolejna = await fetchCandidates(group.id, islandId, debounced, kursor);
      // dopisujemy z odsianiem po id: zaznaczenie kogoś i zapis zmieniają zbiór pod ręką,
      // a podwójny wiersz w liście wygląda na dwie różne osoby o tym samym nazwisku
      setPorcje((prev) => {
        const znane = new Set(prev.map((p) => p.id));
        return [...prev, ...kolejna.rows.filter((r) => !znane.has(r.id))];
      });
      setKursor(kolejna.next);
      setTotal(kolejna.total);
    } finally {
      setDoczytuje(false);
    }
  };

  const dirty = toAdd.size + toRemove.size > 0;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateMembers(group.id, { add: [...toAdd], remove: [...toRemove] });
      toast.success(t('grp.membersSaved'));
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={`${t('grp.membersTitle')} — ${group.name}`}
        description={`${t('grp.code')}: ${group.spsGroupNumber} · ${t('grp.members')}: ${group.memberCount}`}
        className="w-[min(56rem,calc(100vw-2rem))]"
      >
        {group.rationingCount > 0 && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
            <Gauge className="mt-0.5 size-3.5 shrink-0" />
            <span>{t('grp.limitsWarn').replace('{n}', String(group.rationingCount))}</span>
          </div>
        )}

        {/* jedna wspólna implementacja przenośnika — patrz komentarz w TransferPicker */}
        <TransferPicker
          assignedTitle={t('grp.inGroup')}
          availableTitle={t('grp.available')}
          assigned={(members.data?.members ?? []).map((m) => ({
            id: m.id,
            key: m.spsUserNumber,
            primary: m.name,
            badge:
              m.machineCount > 1 ? (
                <Badge variant="warning" title={t('emp.multiHint')}>
                  <Split className="size-2.5" />
                  {m.machineCount}
                </Badge>
              ) : undefined,
          }))}
          candidates={porcje.map((c) => ({ id: c.id, key: c.spsUserNumber, primary: c.name }))}
          candidatesTotal={total}
          hasMore={kursor !== null}
          loadingMore={doczytuje}
          onLoadMore={() => void doczytaj()}
          toAdd={toAdd}
          toRemove={toRemove}
          onToggleAdd={(id) => toggleIn(setToAdd, id)}
          onToggleRemove={(id) => toggleIn(setToRemove, id)}
          search={search}
          onSearchChange={setSearch}
          loadingAssigned={members.isPending}
          loadingCandidates={candidates.isPending}
          emptyAssigned={t('grp.noMembers')}
          emptyCandidates={t('grp.noCandidates')}
        />

        <div className="mt-3">
          <ModalError>{error}</ModalError>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={!dirty || saving} onClick={() => void save()}>
            {saving && <Loader2 className="animate-spin" />}
            {dirty ? `${t('common.save')} (+${toAdd.size} −${toRemove.size})` : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────── dostęp grupy do automatów ───────────────────────────

/**
 * DO KTÓRYCH AUTOMATÓW MA DOSTĘP TA GRUPA (`UserGroups_VendingMachineAssignment`).
 *
 * ⚠️ TA FUNKCJA ISTNIAŁA, ale tylko od drugiej strony: na Przypisaniach wybiera się AUTOMAT
 * i dokłada do niego grupy. Zgłoszone jako brak, bo z ekranu Grup nie było do niej dojścia —
 * a to jest pytanie zadawane właśnie o grupę: „gdzie ci ludzie mogą pobierać". To samo
 * powiązanie, drugi koniec.
 *
 * ⚠️ LISTA Z PTASZKAMI, NIE PRZENOŚNIK. Automatów jest na wyspie kilka–kilkanaście, więc dwie
 * kolumny z przekładaniem byłyby cięższe od problemu; ptaszki pokazują od razu również te
 * NIEprzypisane, czyli odpowiedź na drugą połowę pytania.
 *
 * ⚠️ WYSYŁAMY RÓŻNICĘ względem stanu wczytanego, a nie zaznaczenie. Dwie osoby na tym samym
 * ekranie inaczej kasowałyby sobie zmiany po cichu.
 */
function MachinesModal({
  group,
  islandId,
  canEdit,
  onClose,
  onSaved,
}: {
  group: GroupRow;
  islandId: string;
  /**
   * ⚠️ ODCZYT DLA WSZYSTKICH, ZAPIS DLA ADMINA — tak samo jak w API. Pokazanie przycisku
   * „Zapisz" komuś, kto dostanie 403, znaczyłoby, że ekran obiecuje coś, czego nie dowozi.
   */
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [wybrane, setWybrane] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dane = useQuery({
    queryKey: ['employee-groups', group.id, 'machines', islandId],
    queryFn: () => fetchGroupMachines(group.id, islandId),
  });

  /** Stan początkowy z serwera — punkt odniesienia dla różnicy. */
  const poczatkowe = useMemo(
    () => new Set((dane.data?.machines ?? []).filter((m) => m.assigned).map((m) => m.id)),
    [dane.data],
  );

  useEffect(() => {
    if (dane.data) setWybrane(new Set(poczatkowe));
  }, [dane.data, poczatkowe]);

  const maszyny = dane.data?.machines ?? [];
  const zbior = wybrane ?? poczatkowe;
  const dodane = [...zbior].filter((id) => !poczatkowe.has(id));
  const zdjete = [...poczatkowe].filter((id) => !zbior.has(id));
  const dirty = dodane.length + zdjete.length > 0;
  const wszystkie = maszyny.length > 0 && maszyny.every((m) => zbior.has(m.id));
  const osob = dane.data?.group.memberCount ?? group.memberCount;

  const przelacz = (id: string) =>
    setWybrane((prev) => {
      const next = new Set(prev ?? poczatkowe);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateGroupMachines(group.id, { add: dodane, remove: zdjete });
      toast.success(t('grp.machinesSaved'));
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={`${t('grp.machinesTitle')} — ${group.name}`}
        description={`${t('grp.code')}: ${group.spsGroupNumber} · ${t('grp.machinesHeads').replace('{n}', String(osob))}`}
      >
        <FormGrid>
          {/*
            ⚠️ BEZ AKAPITU O SUMIE DWÓCH DRÓG (zgłoszone 01.09.2026). Wyjaśnienie, dlaczego po
            odznaczeniu ktoś dalej pobiera (bo ma automat przypisany bezpośrednio), stoi teraz przy
            regule w kodzie — a nie nad każdą listą automatów w oknie grupy.
          */}
          {dane.isPending && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
            </p>
          )}
          {dane.isError && <ModalError>{(dane.error as Error).message}</ModalError>}

          {dane.data && maszyny.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('grp.machinesNone')}</p>
          )}

          {maszyny.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  {t('grp.machinesSelected')
                    .replace('{n}', String(zbior.size))
                    .replace('{total}', String(maszyny.length))}
                </span>
                {canEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setWybrane(wszystkie ? new Set() : new Set(maszyny.map((m) => m.id)))}
                  >
                    {wszystkie ? t('grp.machinesClear') : t('grp.machinesAll')}
                  </Button>
                )}
              </div>
              <div className="max-h-64 overflow-y-auto rounded-md border border-border p-1">
                {maszyny.map((m) => (
                  <label
                    key={m.id}
                    className={`flex items-center gap-2 rounded-md p-1.5 text-sm ${
                      canEdit ? 'cursor-pointer hover:bg-accent/50' : 'opacity-70'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="size-3.5 shrink-0 accent-[var(--primary)]"
                      checked={zbior.has(m.id)}
                      disabled={!canEdit}
                      onChange={() => przelacz(m.id)}
                    />
                    <span className="min-w-0">
                      <span className="block leading-snug">{m.name}</span>
                      <span className="block font-mono text-[10px] leading-tight text-muted-foreground">
                        VMID {m.vmid}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">{t('grp.machinesAlsoIn')}</p>
            </div>
          )}

          <ModalError>{error}</ModalError>
        </FormGrid>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {canEdit ? t('common.cancel') : t('common.close')}
          </Button>
          {canEdit && (
            <Button type="button" disabled={!dirty || saving} onClick={() => void save()}>
              {saving && <Loader2 className="animate-spin" />}
              {dirty ? `${t('common.save')} (+${dodane.length} −${zdjete.length})` : t('common.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────── poza wszystkimi grupami ───────────────────────────

/**
 * Pracownicy, którzy nie należą do ŻADNEJ grupy.
 *
 * ⚠️ NIE JEST TO WIDOK „DLA PORZĄDKU”. Reguły limitów celują między innymi w grupy, więc osoba
 * poza wszystkimi grupami nie jest objęta żadną regułą grupową — a przenośnik składu pokazuje
 * zawsze JEDNĄ grupę, więc nie ma z niego jak zobaczyć kogoś, kto nie jest nigdzie.
 *
 * Okno od razu pozwala przypisać zaznaczonych do wybranej grupy: sama lista mówiłaby „masz
 * problem” i nie dawała nic, czym go rozwiązać.
 */
function WithoutGroupModal({
  islandId,
  groups,
  onClose,
  onSaved,
}: {
  islandId: string;
  groups: GroupRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [zaznaczeni, setZaznaczeni] = useState<Set<string>>(new Set());
  const [docelowa, setDocelowa] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const PAGE_SIZE = 25;

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const lista = useQuery({
    queryKey: ['employee-groups', 'bez-grupy', islandId, debounced, page],
    queryFn: () => fetchWithoutGroup(islandId, { search: debounced || undefined, page, pageSize: PAGE_SIZE }),
  });

  const total = lista.data?.total ?? 0;
  const stron = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const przypisz = async () => {
    if (!docelowa || zaznaczeni.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      await updateMembers(docelowa, { add: [...zaznaczeni], remove: [] });
      toast.success(t('grp.membersSaved'));
      setZaznaczeni(new Set());
      onSaved();
      await lista.refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={t('grp.withoutGroupTitle')}
        description={t('grp.withoutGroupDesc')}
        className="w-[min(46rem,calc(100vw-2rem))]"
      >
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('table.search')}
          className="h-8 text-sm"
        />

        <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-border p-1">
          {lista.isPending && <p className="p-2 text-xs text-muted-foreground">{t('common.loading')}</p>}
          {!lista.isPending && (lista.data?.rows.length ?? 0) === 0 && (
            <p className="p-2 text-xs text-muted-foreground">{t('grp.withoutGroupEmpty')}</p>
          )}
          {(lista.data?.rows ?? []).map((e: WithoutGroupRow) => (
            <label
              key={e.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent"
            >
              <input
                type="checkbox"
                className="size-3.5 shrink-0 accent-[var(--primary)]"
                checked={zaznaczeni.has(e.id)}
                onChange={() => toggleIn(setZaznaczeni, e.id)}
              />
              <span className="font-mono text-[10px] text-muted-foreground">{e.spsUserNumber}</span>
              <span className="min-w-0 flex-1 truncate">{e.name}</span>
              {/* 1 albo 2 znaczy, że automat NIE pilnuje limitów tej osoby — a ona i tak
                  nie jest objęta żadną regułą grupową, więc to podwójny brak nadzoru */}
              {e.rationEffect !== 3 && (
                <Badge variant="warning" title={t('emp.rationOff')}>
                  {e.rationEffect}
                </Badge>
              )}
              {e.machineCount > 1 && (
                <Badge variant="warning" title={t('emp.multiHint')}>
                  <Split className="size-2.5" />
                  {e.machineCount}
                </Badge>
              )}
            </label>
          ))}
        </div>

        {stron > 1 && (
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t('grp.withoutGroupCount').replace('{n}', String(total))} · {page} / {stron}
            </span>
            <span className="flex gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ←
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7"
                disabled={page >= stron}
                onClick={() => setPage((p) => p + 1)}
              >
                →
              </Button>
            </span>
          </div>
        )}

        {/* ⚠️ Zaznaczenie przeżywa zmianę strony (trzymamy zbiór id, nie indeksy wierszy),
            więc licznik mówi o CAŁYM zaznaczeniu, a nie o tym, co akurat widać. */}
        <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-border pt-3">
          <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
            {t('grp.assignTo')}
            <select
              value={docelowa}
              onChange={(e) => setDocelowa(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">{t('grp.assignToPick')}</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} ({g.memberCount})
                </option>
              ))}
            </select>
          </label>
          <Button
            type="button"
            disabled={!docelowa || zaznaczeni.size === 0 || saving}
            onClick={() => void przypisz()}
          >
            {saving && <Loader2 className="animate-spin" />}
            {t('grp.assignAction').replace('{n}', String(zaznaczeni.size))}
          </Button>
        </div>

        <div className="mt-3">
          <ModalError>{error}</ModalError>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── strona ──────────────────────────────────────────

export function EmployeeGroups() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { groupId, island } = useIslands();

  const [state, setState] = useState<DataTableState>({
    search: '',
    page: 1,
    pageSize: 25,
    sort: 'name',
    dir: 'asc',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<GroupRow | null>(null);
  const [membersFor, setMembersFor] = useState<GroupRow | null>(null);
  const [machinesFor, setMachinesFor] = useState<GroupRow | null>(null);
  const [operacjeFor, setOperacjeFor] = useState<GroupRow | null>(null);
  const [kodFor, setKodFor] = useState<GroupRow | null>(null);
  /** Usunięcie grupy z kaskadą — okno ze skutkami i blokadami (13.10.2026). */
  const [usunFor, setUsunFor] = useState<GroupRow | null>(null);
  const [bezGrupyOpen, setBezGrupyOpen] = useState(false);

  const params = { ...state, groupId: groupId ?? '' };

  /**
   * Licznik osób poza wszystkimi grupami — pobierany jedną stroną o rozmiarze 1.
   * Interesuje nas tu wyłącznie `total`; wiersze wczytuje dopiero okno.
   */
  const bezGrupy = useQuery({
    queryKey: ['employee-groups', 'bez-grupy', 'licznik', groupId],
    queryFn: () => fetchWithoutGroup(groupId!, { page: 1, pageSize: 1 }),
    enabled: Boolean(groupId),
  });

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: ['employee-groups', params],
    queryFn: () => listGroups(params),
    enabled: Boolean(groupId),
  });

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);
  const invalidate = useCallback(() => void qc.invalidateQueries({ queryKey: ['employee-groups'] }), [qc]);

  const columns = useMemo<ColumnDef<GroupRow>[]>(
    () => [
      {
        id: 'spsGroupNumber',
        // kod grupy jest TEKSTEM, wiec wyrownanie do lewej; wczesniej kolumna nazywala sie
        // USERGROUPID i byla wyrownana do prawej jak liczba
        header: t('grp.code'),
        meta: { exportValue: (r) => r.spsGroupNumber },
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.spsGroupNumber}</span>,
      },
      {
        id: 'name',
        header: t('grp.name'),
        meta: { exportValue: (r) => r.name },
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <span className="font-medium">{row.original.name}</span>
            {row.original.pending && (
              <Badge variant="warning" title={t('grp.pendingHint')}>
                <Clock className="size-3" />
                {t('machines.pending')}
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: 'memberCount',
        header: t('grp.members'),
        enableSorting: false,
        meta: { exportValue: (r) => r.memberCount, align: 'right' },
        cell: ({ row }) => <span className="tabular-nums">{row.original.memberCount}</span>,
      },
      {
        id: 'machineCount',
        header: t('grp.machines'),
        enableSorting: false,
        meta: { exportValue: (r) => r.machineCount, align: 'right' },
        /**
         * ⚠️ LICZBA JEST KLIKALNA. To ona jest pytaniem („do ilu automatów?"), więc odpowiedź
         * powinna być pod nią, a nie tylko pod ikoną na końcu wiersza. Zero też klika się
         * normalnie — „do żadnego" to najczęstszy powód, żeby tu wejść.
         */
        cell: ({ row }) => (
          <button
            type="button"
            className="tabular-nums underline decoration-dotted underline-offset-2 hover:text-[var(--primary)]"
            title={t('grp.machinesTitle')}
            onClick={() => setMachinesFor(row.original)}
          >
            {row.original.machineCount}
          </button>
        ),
      },
      {
        id: 'rationingCount',
        header: t('grp.limits'),
        enableSorting: false,
        meta: { exportValue: (r) => r.rationingCount, align: 'right' },
        cell: ({ row }) =>
          row.original.rationingCount > 0 ? (
            <Badge variant="neutral" title={t('grp.limitsHint')}>
              <Gauge className="size-3" />
              {row.original.rationingCount}
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'actions',
        header: t('table.actions'),
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              title={t('grp.membersTitle')}
              aria-label={`${t('grp.membersTitle')} ${row.original.name}`}
              onClick={() => setMembersFor(row.original)}
            >
              <Users />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title={t('grp.machinesTitle')}
              aria-label={`${t('grp.machinesTitle')} ${row.original.name}`}
              onClick={() => setMachinesFor(row.original)}
            >
              <Boxes />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title={t('ops.title')}
              aria-label={`${t('ops.title')} ${row.original.name}`}
              onClick={() => setOperacjeFor(row.original)}
            >
              <KeySquare />
            </Button>
            {isAdmin && (
              <>
                {/* ⚠️ OSOBNY PRZYCISK, NIE POLE W OKNIE EDYCJI. Kod grupy jest kluczem naturalnym
                    rozsianym po sześciu tabelach SPS — gdyby dało się go nadpisać przy poprawianiu
                    nazwy, literówka uruchamiałaby kaskadę na bazie klienta. */}
                <Button
                  variant="ghost"
                  size="icon"
                  title={t('grp.codeChangeAction')}
                  aria-label={`${t('grp.codeChangeAction')} ${row.original.name}`}
                  onClick={() => setKodFor(row.original)}
                >
                  <KeyRound />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title={t('common.edit')}
                  aria-label={`${t('common.edit')} ${row.original.name}`}
                  onClick={() => {
                    setEditing(row.original);
                    setModalOpen(true);
                  }}
                >
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title={t('grp.usun.akcja')}
                  aria-label={`${t('grp.usun.akcja')} ${row.original.name}`}
                  onClick={() => setUsunFor(row.original)}
                >
                  <Trash2 />
                </Button>
              </>
            )}
          </div>
        ),
      },
    ],
    [t, isAdmin],
  );

  const fetchAllRows = useCallback(async (): Promise<GroupRow[]> => {
    if (!groupId) return [];
    const out: GroupRow[] = [];
    for (let page = 1; ; page += 1) {
      const chunk = await listGroups({ ...params, page, pageSize: 200 });
      out.push(...chunk.rows);
      if (out.length >= chunk.total || chunk.rows.length === 0) break;
    }
    return out;
  }, [params, groupId]);

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  return (
    <>
      {/* ⚠️ SYGNAŁ POKAZUJEMY TYLKO, GDY JEST CO POKAZAĆ. Pusty pasek „0 osób bez grupy”
          stałby na ekranie zawsze i po tygodniu nikt by go nie czytał — a wtedy nie zauważy
          go też w dniu, w którym liczba przestanie być zerem. */}
      {(bezGrupy.data?.total ?? 0) > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-4 text-sm">
          <UserX className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            {t('grp.withoutGroupBanner').replace('{n}', String(bezGrupy.data!.total))}
          </span>
          <Button type="button" variant="outline" size="sm" onClick={() => setBezGrupyOpen(true)}>
            {t('grp.withoutGroupShow')}
          </Button>
        </div>
      )}

      <DataTable<GroupRow>
        title={t('nav.groups')}
        columns={columns}
        data={data?.rows ?? []}
        total={data?.total ?? 0}
        state={state}
        onStateChange={onStateChange}
        isLoading={isPending}
        isFetching={isFetching}
        onRefresh={() => void refetch()}
        rowKey={(r) => r.id}
        exportFileName={`grupy-${island?.code ?? 'wyspa'}`}
        fetchAllRows={fetchAllRows}
        reportKey="groups"
        reportGroupId={groupId}
        actions={
          isAdmin ? (
            <Button
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              <Plus />
              {t('grp.add')}
            </Button>
          ) : null
        }
      />

      {modalOpen && (
        <GroupModal
          key={editing?.id ?? 'new'}
          open={modalOpen}
          onOpenChange={setModalOpen}
          editing={editing}
          groupId={groupId}
          onSaved={invalidate}
        />
      )}

      {membersFor && (
        <MembersModal
          group={membersFor}
          islandId={groupId}
          onClose={() => setMembersFor(null)}
          onSaved={invalidate}
        />
      )}

      {usunFor && (
        <UsuniecieGrupyPracownikow
          grupaId={usunFor.id}
          islandId={groupId}
          onClose={() => setUsunFor(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['employee-groups'] })}
        />
      )}

      {kodFor && (
        <ZmianaKoduGrupy
          grupaId={kodFor.id}
          islandId={groupId}
          obecnyKod={kodFor.spsGroupNumber}
          nazwa={kodFor.name}
          onClose={() => setKodFor(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['employee-groups'] })}
        />
      )}

      {operacjeFor && (
        <OperacjeModal
          cel="group"
          id={operacjeFor.id}
          islandId={groupId}
          canEdit={isAdmin}
          onClose={() => setOperacjeFor(null)}
          onSaved={invalidate}
        />
      )}

      {machinesFor && (
        <MachinesModal
          group={machinesFor}
          islandId={groupId}
          canEdit={isAdmin}
          onClose={() => setMachinesFor(null)}
          onSaved={invalidate}
        />
      )}

      {bezGrupyOpen && (
        <WithoutGroupModal
          islandId={groupId}
          groups={data?.rows ?? []}
          onClose={() => setBezGrupyOpen(false)}
          onSaved={invalidate}
        />
      )}
    </>
  );
}
